import { useUser } from "@clerk/clerk-react";
import { ArrowRight, Sparkles } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import TermsAcceptance from "../components/TermsAcceptance";
import { apiClient } from "../lib/apiClient";

export default function Start() {
	const navigate = useNavigate();
	const { user, isLoaded } = useUser();
	const [isLoading, setIsLoading] = useState(false);
	const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [showTimeoutModal, setShowTimeoutModal] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);

	// Cleanup polling on component unmount
	useEffect(() => {
		return () => {
			if (pollingInterval) {
				console.log("Cleaning up polling interval on component unmount");
				clearInterval(pollingInterval);
			}
		};
	}, [pollingInterval]);

	const startOnboarding = async () => {
		setIsLoading(true);

		let interval: NodeJS.Timeout | null = null;
		let timeout: NodeJS.Timeout | null = null;
		let isCompleted = false;

		const cleanup = () => {
			if (interval) {
				clearInterval(interval);
				setPollingInterval(null);
			}
			if (timeout) {
				clearTimeout(timeout);
			}
		};

		const checkUserExists = async () => {
			if (isCompleted) return;

			try {
				const response = await apiClient().get<{ userExists: boolean }>(`/user/exists/${user?.id}`);

				if (response.data.userExists && !isCompleted) {
					isCompleted = true;
					cleanup();

					// Proceed with onboarding flow
					const result = await apiClient().post<{ accountId: string }>(
						`/connect/account/${user?.id}`,
					);
					navigate(`/start/${result.data.accountId}`);
					setIsLoading(false);
				}
			} catch (error) {
				console.error("Failed to check user existence:", error);
				if (!isCompleted) {
					isCompleted = true;
					cleanup();
					setShowErrorModal(true);
					setIsLoading(false);
				}
			}
		};

		try {
			// Start polling every 2 seconds
			interval = setInterval(checkUserExists, 2000);
			setPollingInterval(interval);

			// Also check immediately
			await checkUserExists();

			// Set timeout after 20 seconds
			timeout = setTimeout(() => {
				if (!isCompleted) {
					console.log("Polling Timeout: Connection timed out after 20 seconds");
					isCompleted = true;
					cleanup();
					setIsLoading(false);
					setShowTimeoutModal(true);
				}
			}, 20000);
		} catch (error) {
			console.error("Failed to start onboarding:", error);
			cleanup();
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen w-full flex">
			{/* Modal for error handling */}
			<Modal
				isOpen={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				onClick={() => {
					setShowErrorModal(false);
					startOnboarding();
				}}
				title="This is embarassing!"
			>
				<p className="text-gray-500 text-base leading-relaxed">
					We encountered an error while starting the setup process. We've notified the team!
				</p>
			</Modal>

			{/* Modal for timeout handling */}
			<Modal
				isOpen={showTimeoutModal}
				onClick={() => {
					setShowTimeoutModal(false);
					startOnboarding();
				}}
				onClose={() => setShowErrorModal(false)}
				title="Sorry about this!"
			>
				<p className="text-gray-500 text-base leading-relaxed">
					We're just waiting for your Sehara account to be finalized before we can continue.
				</p>

				<p className="text-gray-500 text-base leading-relaxed mt-6">
					Try clicking the button again in a minute or two.
				</p>
			</Modal>

			{/* Left side - Content */}
			<div className="w-full md:w-1/2 bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
				<div className="h-full flex items-center justify-center p-12">
					<div className="max-w-xl space-y-8">
						<div className="space-y-4">
							<div className="flex items-center gap-2 text-orange-500">
								<Sparkles className="w-6 h-6" />
								<span className="text-sm font-medium uppercase tracking-wide">Welcome aboard</span>
							</div>
							<h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
								Let's get started
								<span className="block text-orange-500">setting up your</span>
								<span className="block text-pink-500">account</span>
							</h1>
							<p className="text-xl text-gray-600">
								We're thrilled to have you here! Let's make your experience absolutely delightful
								from the very beginning.
							</p>
						</div>

						{/* Terms and Conditions Section */}
						<TermsAcceptance termsAccepted={termsAccepted} onTermsChange={setTermsAccepted} />

						<div className="space-y-4">
							{!isLoaded && (
								<button
									type="button"
									className="group bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-3"
									disabled={true}
									aria-label="Loading button"
								>
									<Spinner />
								</button>
							)}
							{isLoaded && (
								<button
									type="button"
									onClick={startOnboarding}
									className={`group font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-lg flex items-center gap-3 ${
										termsAccepted && !isLoading
											? "bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white transform hover:scale-105 hover:shadow-xl cursor-pointer"
											: "bg-gray-300 text-gray-500 cursor-not-allowed"
									}`}
									disabled={isLoading || !termsAccepted}
									aria-label={!termsAccepted ? "Accept terms to continue" : "Start onboarding"}
								>
									{isLoading ? (
										<Spinner />
									) : (
										<>
											Let's go
											<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
										</>
									)}
								</button>
							)}
							<p className="text-sm text-gray-500">
								{!termsAccepted
									? "Please accept our terms and conditions to continue ✨"
									: "This will only take a minute, we promise! ✨"}
							</p>
						</div>
					</div>
				</div>
			</div>
			{/* Right side - Image */}
			<div className="hidden md:block w-1/2 relative overflow-hidden">
				<img
					src="https://images.unsplash.com/photo-1679621551238-9c83dd69bc7d?q=80&w=2160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt="Stylish woman with tattoos in an artistic setting"
					className="w-full h-full object-cover"
				/>
				<div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
				<div className="absolute bottom-8 right-8 bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full font-medium text-sm shadow-lg">
					Ready for your journey? 🎉
				</div>
			</div>
		</div>
	);
}
